import { ApolloClient, gql } from '@apollo/client'
import { UserScopedGrowthBook } from '@growthbook/growthbook'
import { ArgosClient } from '@jusbrasil-web/shared-argos-client'
import { parseUserCookie, UserSession } from '@jusbrasil-web/shared-user-session'
import { GetServerSidePropsContext } from 'next'
import { UAParser } from 'ua-parser-js'

import {
  EXPERIMENT_PARTICIPATE_EVENT_NAME,
  FEATURE_FLAGS_INTERNAL_STATE,
  NETWORK_TIMEOUT_MS,
} from '../constants'
import { AppPropsWithFeatureFlags, TrackedExperiments, UserData, WithFeatureFlags } from '../types'
import { GetProfileForFeatureFlagsQuery } from './__generated__/with-feature-flags.graphql'
import { getClient } from './client'

const argosClient = new ArgosClient()
const enableDevMode = process.env.NEXT_PUBLIC_GROWTHBOOK_DEV_MODE === 'true'

const SNAKE_CASE_PATTERN = /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/

function validateFeatureKeys(keys: string[]) {
  const invalid = keys.filter((key) => !SNAKE_CASE_PATTERN.test(key))
  if (invalid.length > 0) {
    throw new Error(
      `[shared-feature-flags] feature keys must be lowercase snake_case. Invalid: ${invalid.join(
        ', '
      )}`
    )
  }
}

const GET_PROFILE_FOR_FEATURE_FLAGS = gql`
  query getProfileForFeatureFlags {
    root {
      viewer: me {
        pid
        user {
          email
          isAdmin
        }
      }
    }
  }
`

async function getUserData(
  ctx: GetServerSidePropsContext,
  apolloClient: ApolloClient<unknown>
): Promise<UserData> {
  const userSession: UserSession = parseUserCookie(ctx)
  const device = new UAParser(ctx.req?.headers?.['user-agent'] ?? '').getDevice()
  const deviceType = device.type || 'desktop'
  if (!userSession?.pid) {
    return {
      is_admin: false,
      device_id: userSession?.jdid,
      device_type: deviceType,
    }
  }

  try {
    const { data } = await apolloClient.query<GetProfileForFeatureFlagsQuery>({
      query: GET_PROFILE_FOR_FEATURE_FLAGS,
      context: { fetchOptions: { signal: AbortSignal.timeout(NETWORK_TIMEOUT_MS) } },
    })
    const viewer = data?.root?.viewer
    if (viewer) {
      const { pid, user } = viewer
      return {
        pid,
        device_id: userSession.jdid,
        email: user?.email,
        is_admin: !!user?.isAdmin,
        device_type: deviceType,
      }
    }
  } catch (e) {
    console.error(
      '[shared-feature-flags] failed to load viewer profile, falling back to cookie attrs',
      e
    )
  }

  return {
    pid: userSession.pid,
    device_id: userSession.jdid,
    is_admin: false,
    device_type: deviceType,
  }
}

export const withFeatureFlags: WithFeatureFlags = async (
  ctx,
  apolloClient,
  featuresWithDefaultValue,
  options
) => {
  type F = typeof featuresWithDefaultValue

  validateFeatureKeys(Object.keys(featuresWithDefaultValue))

  const [userData, client] = await Promise.all([getUserData(ctx, apolloClient), getClient()])
  const jdid = userData.device_id

  const trackedExperiments: TrackedExperiments = {}
  const instance: UserScopedGrowthBook<typeof featuresWithDefaultValue> =
    client.createScopedInstance({
      enableDevMode,
      attributes: { ...options?.extraUserAttributes, ...userData },
      trackingCallback: (experiment, result) => {
        // Key by the feature id (not experiment.key): getFeatureValue and evalFeature
        // operate on feature keys, and a feature key can differ from its experiment
        // key. featureId falls back to experiment.key for inline experiments that
        // aren't backed by a feature flag.
        const featureKey = result.featureId ?? experiment.key
        trackedExperiments[featureKey] = {
          experimentKey: experiment.key,
          variationName: result.name ?? null,
        }
      },
    })

  const featureFlags: F = { ...featuresWithDefaultValue }
  for (const key in featuresWithDefaultValue) {
    if (Object.prototype.hasOwnProperty.call(featuresWithDefaultValue, key)) {
      const featureResult = instance.evalFeature(key)
      featureFlags[key] = featureResult.value ?? featuresWithDefaultValue[key]
    }
  }

  const sentExperiments = new Set<string>()

  return {
    getFeatureValue(key) {
      const value = featureFlags[key]
      const tracked = trackedExperiments[key]
      if (tracked && !sentExperiments.has(key)) {
        sentExperiments.add(key)
        argosClient.send(userData.pid?.toString(), EXPERIMENT_PARTICIPATE_EVENT_NAME, {
          experiment: tracked.experimentKey,
          alternative: tracked.variationName,
          device_id: jdid ?? null,
        })
      }
      return value
    },
    withFeatureFlagsProps() {
      const props: AppPropsWithFeatureFlags<F> = {
        [FEATURE_FLAGS_INTERNAL_STATE]: {
          featureFlags,
          trackedExperiments,
        },
      }
      return props
    },
  }
}
